import React, { useMemo } from 'react';
import { Sym } from '../../../components/app2';

/**
 * Mapa de cobertura: un año, una fila por mes, una celda por día.
 *
 * Responde de un vistazo a "¿de qué días tengo recibos y cuáles nunca le pedí
 * al SAT?". El eje es la fecha de EMISIÓN del recibo, que es por donde filtra el
 * SAT — no el periodo de nómina que agrupa la pantalla de Recibos. Son dos ejes
 * distintos y por eso el encabezado lo dice explícitamente: si no, parecería
 * que la app se contradice consigo misma.
 */

export const ESTADOS = {
  con_recibos: {
    etiqueta: 'Con recibos',
    color: 'var(--ok)',
    fondo: 'var(--ok)',
    texto: '#fff',
  },
  pedido_sin_recibos: {
    etiqueta: 'Pedido, sin recibos',
    color: 'var(--brand)',
    fondo: 'var(--brand-soft)',
    texto: 'var(--brand)',
  },
  en_proceso: {
    etiqueta: 'Pidiéndose ahora',
    color: 'var(--warn)',
    fondo: 'var(--warn-soft)',
    texto: 'var(--warn)',
  },
  fallo: {
    etiqueta: 'No se pudo',
    color: 'var(--danger)',
    fondo: 'var(--danger-soft)',
    texto: 'var(--danger)',
  },
  sin_pedir: {
    etiqueta: 'Sin pedir',
    color: 'var(--line-2)',
    fondo: 'var(--surface-2)',
    texto: 'var(--faint)',
  },
};

// Capitalizados en el texto y no por CSS: lo que se lee en pantalla debe ser
// lo que hay en el DOM, para quien use lector de pantalla y para las pruebas.
const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const diaDe = (iso) => Number(String(iso).slice(8, 10));
const mesDe = (iso) => Number(String(iso).slice(5, 7)) - 1;

const cuandoSePidio = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const PEDIDOS = ['con_recibos', 'pedido_sin_recibos'];

/** Lo que se lee al pasar el cursor: sin sorpresas, todo lo que se sabe del día. */
const explicar = (d) => {
  const partes = [`${d.dia} · ${ESTADOS[d.estado]?.etiqueta || d.estado}`];
  if (d.estado === 'con_recibos') partes.push(`${d.recibos} recibo(s) emitido(s) ese día`);
  if (d.solicitado_en) partes.push(`Se pidió el ${cuandoSePidio(d.solicitado_en)}`);
  if (d.solicitud_id) partes.push(`Solicitud #${d.solicitud_id}`);
  if (d.estado === 'fallo' && d.consumio_cupo) partes.push('Ese intento sí consumió un cupo del SAT');
  if (d.estado === 'sin_pedir') partes.push('Nunca se le ha pedido al SAT');
  // Un día pedido pronto no está terminado: caben timbres tardíos.
  if (PEDIDOS.includes(d.estado) && !d.definitivo) {
    partes.push('Todavía puede recibir recibos timbrados tarde: el barrido lo repetirá');
  }
  return partes.join(' · ');
};

const Celda = ({ dia, onAbrir }) => {
  const cfg = ESTADOS[dia.estado] || ESTADOS.sin_pedir;
  const clicable = Boolean(dia.solicitud_id);

  return (
    <button
      type="button"
      className="a2-cob-celda"
      title={explicar(dia)}
      aria-label={explicar(dia)}
      data-estado={dia.estado}
      data-dia={dia.dia}
      data-provisional={PEDIDOS.includes(dia.estado) && !dia.definitivo ? 'si' : undefined}
      data-solicitud={dia.solicitud_id || undefined}
      onClick={clicable ? () => onAbrir?.(dia) : undefined}
      style={{
        background: dia.estado === 'con_recibos' ? cfg.fondo : cfg.fondo,
        border: `1px solid ${dia.estado === 'sin_pedir' ? 'var(--line)' : cfg.color}`,
        color: dia.estado === 'con_recibos' ? cfg.texto : cfg.texto,
        cursor: clicable ? 'pointer' : 'default',
      }}
    >
      {diaDe(dia.dia)}
    </button>
  );
};

const MapaCobertura = ({ dias = [], onAbrirSolicitud }) => {
  // Se agrupa por mes conservando el orden que trae el servidor.
  const meses = useMemo(() => {
    const porMes = new Map();
    for (const d of dias) {
      const clave = String(d.dia).slice(0, 7);
      if (!porMes.has(clave)) porMes.set(clave, []);
      porMes.get(clave).push(d);
    }
    return [...porMes.entries()].map(([clave, suyos]) => ({
      clave,
      nombre: MESES[mesDe(suyos[0].dia)],
      anio: String(clave).slice(0, 4),
      dias: suyos,
      recibos: suyos.reduce((n, x) => n + x.recibos, 0),
    }));
  }, [dias]);

  if (!dias.length) return null;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {meses.map((m) => (
        <div key={m.clave}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span style={{ font: "700 .92rem 'Hanken Grotesk'", color: 'var(--heading)' }}>
              {m.nombre}
            </span>
            <span className="a2-tcell-muted">
              {m.recibos > 0 ? `${m.recibos} recibo(s)` : 'sin recibos'}
            </span>
          </div>
          <div className="a2-cob-mes">
            {m.dias.map((d) => <Celda key={d.dia} dia={d} onAbrir={onAbrirSolicitud} />)}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {/* Clase propia, NO la de las celdas: la muestra de la leyenda no es
              un día y contarla como tal falsearía cualquier conteo del año. */}
          <span
            className="a2-cob-muestra"
            style={{
              width: 13, height: 13, borderRadius: 4, flexShrink: 0,
              background: 'var(--ok)', border: '1px solid var(--ok)',
            }}
          />
          <span className="a2-tcell-muted">Aún puede llegar más (72 h de timbrado)</span>
        </span>
        {Object.entries(ESTADOS).map(([clave, cfg]) => (
          <span key={clave} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 13, height: 13, borderRadius: 4, flexShrink: 0,
                background: cfg.fondo, border: `1px solid ${clave === 'sin_pedir' ? 'var(--line)' : cfg.color}`,
              }}
            />
            <span className="a2-tcell-muted">{cfg.etiqueta}</span>
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <Sym name="info" size={15} style={{ color: 'var(--muted)', flexShrink: 0, marginTop: 2 }} />
        <span className="a2-tcell-muted">
          Los días son de <strong>emisión del recibo</strong>, que es por donde el SAT filtra al
          entregarlos. En la pantalla de Recibos se agrupan por <strong>periodo de nómina</strong>:
          un recibo de la quincena del 1 al 15 emitido el 17 aparece aquí en el 17.
          {' '}Los días marcados con punto siguen abiertos: la ley da 72 horas para timbrar, así
          que el barrido de cada noche los vuelve a pedir hasta que esa ventana cierra.
        </span>
      </div>
    </div>
  );
};

export default MapaCobertura;
